import { getApiUrl } from '@/utils/ajax';
import {
    BygrModule,
    COUNTRY_CODES,
    request,
    UserSalutation,
    UserTitle,
} from '@ygtechnologies/bygr-frontend-component-library';
import {
    Profile,
    LocalizedEnum,
    ProfileMemberPermissions,
    ProfileMember,
    ProfileMemberDetails,
    ProfileInfo,
    ExternalLoginProvider,
    ProfileTooltipInfo,
    UnitOfMeasurement,
    ProfileUnitInfo,
    EmailValidity,
} from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { getCoreApiUrl } from '@/utils/ajax';
import { memoize } from 'lodash-es';

export function getMyProfiles(): Promise<ProfileInfo[]> {
    return request(`api/profile`).then((x) => x.json());
}

export async function getMyUnitProfiles(): Promise<ProfileUnitInfo[]> {
    const retur = await request(
        `v1/profile/buyer`,
        {
            method: 'GET',
            json: true,
        },
        getCoreApiUrl()
    ).then((response) => response.json());
    return retur;
}

export function getProfile(profileId: number): Promise<Profile> {
    return request(`api/profile/${profileId}`).then((x) => x.json());
}

export function getProfileTooltipInfo(profileId: number): Promise<ProfileTooltipInfo> {
    return request(`api/profile/${profileId}/tooltip-info`).then((x) => x.json());
}

export interface ProfileConfiguration {
    name: string;
    subdomain: string;
    loginProviders: ExternalLoginProvider[];
    currencyId: number | null;
    defaultUnitOfMeasurement: UnitOfMeasurement;
    countryCode: keyof typeof COUNTRY_CODES;
}
export function getProfileConfiguration(profileId: number): Promise<ProfileConfiguration> {
    return request(`api/profile/${profileId}/configuration`).then((x) => x.json());
}

export function searchProfiles(options: { text: string }): Promise<Profile[]> {
    return request(`api/profile/public/search`, { query: options }).then((x) => x.json());
}

export interface UpdateProfileOptions {
    name: string;
    subdomain: string;
    loginProviders?: ExternalLoginProvider[];
    image?: FileData | null;
    backgroundImage?: FileData | null;
    defaultUnitOfMeasure?: UnitOfMeasurement;
}

function createProfile(options: UpdateProfileOptions): Promise<Profile> {
    return request(`api/profile`, {
        method: 'POST',
        json: true,
        body: options,
    }).then((x) => x.json());
}

function updateProfile(profileId: number, options: UpdateProfileOptions): Promise<Profile> {
    return request(`api/profile/${profileId}`, {
        method: 'PUT',
        json: true,
        body: options,
    }).then((x) => x.json());
}

export function createOrUpdateProfile(profileId: number | null, options: UpdateProfileOptions): Promise<Profile> {
    return profileId ? updateProfile(profileId, options) : createProfile(options);
}

export function getProfilePermissions(): Promise<LocalizedEnum[]> {
    return request(`api/profile/permission-types`).then((x) => x.json());
}

export function searchProfileMembers(profileId: number, query): Promise<ProfileMember[]> {
    return request(`api/profile/${profileId}/members`, {
        method: 'GET',
        json: true,
        query: { query },
    }).then((x) => x.json());
}

export type ProfileInvitationOptions = ProfileMemberPermissions & {
    inviterProfileId: number;
    inviteeEmail: string;
    inviteePhoneNumber?: string;
    inviteeFirstName?: string;
    inviteeLastName?: string;
    inviteeSalutation?: UserSalutation | null;
    inviteeTitle?: UserTitle | null;
};

export type ProfileInvitationEmail = {
    profileId: number;
    inviteeEmail: string;
};

export function checkProfileInviteEmail(invitation: ProfileInvitationEmail): Promise<EmailValidity> {
    return request(`api/profile-invitation/validate-email`, {
        method: 'POST',
        json: true,
        body: invitation,
    }).then((x) => x.json());
}

export function inviteProfileMember(invitation: ProfileInvitationOptions) {
    return request(`api/profile-invitation`, {
        method: 'POST',
        json: true,
        body: invitation,
    });
}

export function cancelProfileInvite(profileInviteId: number) {
    return request(`api/profile-invitation/${profileInviteId}/cancel`, {
        method: 'POST',
    });
}

export function resendProfileInvite(profileInviteId: number) {
    return request(`api/profile-invitation/${profileInviteId}/resend`, {
        method: 'POST',
    });
}

export function getProfileAcl(profileId: number): Promise<ProfileMemberDetails[]> {
    return request(`api/profile/${profileId}/acl`).then((x) => x.json());
}

export function patchProfileAcl(profileMemberId: number, userPermissionDetails: ProfileMemberPermissions) {
    return request(`api/profile-member/${profileMemberId}/acl`, {
        method: 'PUT',
        json: true,
        body: userPermissionDetails,
    });
}

export function getProfileImageUrl(profileId: number) {
    return getApiUrl() + `/api/profile/${profileId}/image`;
}
export function getProfileBackgroundImageUrl(profileId: number) {
    return getApiUrl() + `/api/profile/${profileId}/background-image`;
}

export function getProfileImageThumbnailUrl(profileId: number) {
    return getApiUrl() + `/api/profile/${profileId}/image/thumbnail`;
}

export interface BygrModuleInfo {
    module: BygrModule;
    projectCount: number;
    canCreateProjects: boolean;
}

export function getProfileModules(profileId: number): Promise<BygrModuleInfo[]> {
    return request(`api/profile/${profileId}/modules`).then((x) => x.json());
}

export const getProfileFeatureToggles = memoize(
    (profileId: number): Promise<string[]> => {
        return request(getCoreApiUrl() + `/v1/profile/${profileId}/features-toggles`, {}).then((x) => x.json());
    },
    (profileId: number) => `${profileId}`
);
